import { useEffect, useState } from 'react';
import { DateTime as DT } from 'luxon';
import { useTranslation } from 'react-i18next';
import { TaskTypeStatus, type LeadType, type ObjectType, type TaskCardType } from '@econtractor/types';
import * as apiTask from 'api/apiTask';
import { useAuth } from 'context/AuthContext';
import { useToast } from 'context/ToastContext';
import FormInput from 'components/forms/FormInput';
import { LeadSelected } from 'modules/lead';
import { ObjectSelected } from 'modules/objects';
import TaskStatusBadge from '../../shared/TaskStatusBadge';
import TaskTermEditForm from '../../shared/TaskTermEditForm';
import TaskTermInterval from '../../shared/TaskTermInterval';
import TaskTypeChip from '../../shared/TaskTypeChip';
import TaskActions from '../TaskActions';
import { tDirName } from 'utils/helpers/i18n/tDirName';
import SolarIcon from 'components/icons/SolarIcon';

interface TaskCardGeneralTabProps {
    task: TaskCardType;
    onTaskUpdated: (task: TaskCardType) => void;
    onRequestResponse: () => void;
    showRejectForm: boolean;
    onShowRejectFormChange: (show: boolean) => void;
}

const formatCreatedAt = (millis: number): string => {
    if (!millis) return '—';
    const dt = DT.fromMillis(millis, { zone: 'local' });
    return dt.isValid ? dt.toFormat('dd.MM.yyyy HH:mm') : '—';
};

const hasHtmlContent = (html: string): boolean => {
    const text = String(html || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .trim();
    return text.length > 0;
};

const toLeadValue = (task: TaskCardType): LeadType | null => {
    if (!task.leadInfo?.ID) return null;
    return {
        ID: task.leadInfo.ID,
        clientName: task.leadInfo.clientName || '',
        clientPhone: task.leadInfo.clientPhone || '',
        leadStatus: Number(task.leadInfo.leadStatus || 0),
    } as LeadType;
};

const toObjectValue = (task: TaskCardType): ObjectType | null => {
    if (!task.objectInfo?.ID) return null;
    return {
        ID: task.objectInfo.ID,
        objectName: task.objectInfo.objectName || '',
        clientName: task.objectInfo.clientName || '',
    } as ObjectType;
};

const TaskCardGeneralTab = ({
    task,
    onTaskUpdated,
    onRequestResponse,
    showRejectForm,
    onShowRejectFormChange,
}: TaskCardGeneralTabProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { auth } = useAuth();
    const { showToast } = useToast();
    const [editingTerm, setEditingTerm] = useState(false);
    const [kanbanHideSaving, setKanbanHideSaving] = useState(false);
    const showDescription = hasHtmlContent(task.taskDescription || '');
    const leadValue = toLeadValue(task);
    const objectValue = toObjectValue(task);

    const userId = Number(auth?.user?.ID || 0);
    const status = Number(task.taskStatus);
    const isOwner = userId > 0 && userId === Number(task.userId);
    const isController = userId > 0 && userId === Number(task.taskController);
    const isDeleted = Boolean(Number(task.taskDeleted));
    const canEditTerm =
        !isDeleted &&
        isController &&
        (status === TaskTypeStatus.NEW || status === TaskTypeStatus.INWORK);
    const canToggleKanbanHide =
        !isDeleted &&
        (isOwner || isController) &&
        status === TaskTypeStatus.ACCEPTED;
    const showKanbanHide = status === TaskTypeStatus.ACCEPTED;

    useEffect(() => {
        if (Number(task.taskStatus) !== TaskTypeStatus.COMPLETED && showRejectForm) {
            onShowRejectFormChange(false);
        }
    }, [task.taskStatus, showRejectForm, onShowRejectFormChange]);

    useEffect(() => {
        if (!canEditTerm) setEditingTerm(false);
    }, [canEditTerm]);

    const handleReject = async (value: string | number) => {
        const comment = String(value || '').trim();
        if (!comment) {
            showToast(t('errEmptyReject'), 'danger');
            return false;
        }
        const result = await apiTask.rejectTaskAnswer(task.ID, { comment });
        if (!result?.stat || !result.task) {
            showToast(
                result?.errCode === 'EMPTY_COMMENT' ? t('errEmptyReject') : t('common:messError'),
                'danger',
            );
            return false;
        }
        showToast(t('messReturnedToWork'), 'success');
        onTaskUpdated(result.task);
        return true;
    };

    const handleKanbanHideChange = async (checked: boolean) => {
        if (!canToggleKanbanHide || kanbanHideSaving) return;
        setKanbanHideSaving(true);
        const result = await apiTask.updateTaskKanbanHide(task.ID, { hide: checked });
        setKanbanHideSaving(false);
        if (!result?.stat || !result.task) {
            showToast(t('common:messError'), 'danger');
            return;
        }
        showToast(t('messKanbanHideUpdated'), 'success');
        onTaskUpdated(result.task);
    };

    return (
        <>
            <div className="task-card-hero mb-3">
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <TaskTypeChip
                        name={tDirName(t, task.taskTypeI18nKey, task.taskTypeName)}
                        icon={task.taskTypeIcon}
                        colors={task.taslTypeJson}
                    />
                    <TaskStatusBadge
                        status={Number(task.taskStatus)}
                        dateEnd={task.dateEnd}
                        taskDeleted={task.taskDeleted}
                    />
                    <TaskActions
                        task={task}
                        onTaskUpdated={onTaskUpdated}
                        onRequestResponse={onRequestResponse}
                        onRequestReject={() => onShowRejectFormChange(!showRejectForm)}
                        rejectFormOpen={showRejectForm}
                    />
                </div>

                {showRejectForm && (
                    <div className="mb-3">
                        <FormInput
                            title={t('labelRejectComment')}
                            showForm={showRejectForm}
                            setShowForm={onShowRejectFormChange}
                            defaultValue={task.taskAnswerReject || ''}
                            as="textarea"
                            rows={5}
                            required
                            requiredMessage={t('errEmptyReject')}
                            callBack={handleReject}
                        />
                    </div>
                )}

                <h4 className="task-card-title mb-1">{task.taskName || '—'}</h4>
                <div className="task-card-meta">
                    <SolarIcon name="clock-history" className="me-1" />
                    {t('fields.created')}: {formatCreatedAt(Number(task.dt || 0))}
                </div>
            </div>

            <section className="task-card-section mb-3">
                <div className="task-card-section__label">{t('fields.taskFor')}</div>
                {objectValue ? (
                    <ObjectSelected
                        viewOnly
                        value={objectValue}
                        title={t('fields.object')}
                        className="mb-0"
                    />
                ) : leadValue ? (
                    <LeadSelected
                        viewOnly
                        value={leadValue}
                        title={t('fields.lead')}
                        className="mb-0"
                    />
                ) : (
                    <div className="task-card-panel">
                        <div className="d-flex align-items-center gap-2 text-muted">
                            <SolarIcon name="person" />
                            <span>{t('fields.personal')}</span>
                        </div>
                    </div>
                )}
            </section>

            <section className="task-card-section mb-3">
                <div className="task-card-section__label">{t('fields.term')}</div>

                {editingTerm ? (
                    <div className="task-card-panel">
                        <TaskTermEditForm
                            task={task}
                            onCancel={() => setEditingTerm(false)}
                            onSaved={(next) => {
                                setEditingTerm(false);
                                onTaskUpdated(next);
                            }}
                        />
                    </div>
                ) : (
                    <div className="task-card-panel">
                        <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-end justify-content-between gap-2">
                            <div className="flex-grow-1 min-w-0">
                                <TaskTermInterval
                                    taskId={task.ID}
                                    dateStart={task.dateStart}
                                    dateEnd={task.dateEnd}
                                    taskStatus={Number(task.taskStatus)}
                                    withTime={Boolean(task.withTime)}
                                />
                            </div>
                            {canEditTerm && (
                                <div className="text-end flex-shrink-0">
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 align-baseline fs-12"
                                        onClick={() => setEditingTerm(true)}
                                    >
                                        <SolarIcon name="pencil" className="me-1 fs-14" />
                                        {t('common:buttons.change')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </section>

            {showDescription && (
                <section className="task-card-section mb-3">
                    <div className="task-card-section__label">{t('labelInfo')}</div>
                    <div
                        className="task-card-description"
                        dangerouslySetInnerHTML={{ __html: task.taskDescription || '' }}
                    />
                </section>
            )}

            {showKanbanHide && (
                <section className="task-card-section mb-0 pt-2">
                    <div className="task-card-panel">
                        <div className="form-check form-switch mb-0">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id={`task-${task.ID}-kanban-hide`}
                                checked={Boolean(task.taskKanbanHide)}
                                disabled={!canToggleKanbanHide || kanbanHideSaving}
                                onChange={(e) => void handleKanbanHideChange(e.currentTarget.checked)}
                            />
                            <label
                                className="form-check-label"
                                htmlFor={`task-${task.ID}-kanban-hide`}
                            >
                                <span className="d-block fs-12">{t('labelKanbanHide')}</span>
                                <span
                                    className="d-block text-muted"
                                    style={{ fontSize: '0.65rem', lineHeight: 1.35 }}
                                >
                                    {t('hintKanbanHide')}
                                </span>
                            </label>
                        </div>
                    </div>
                </section>
            )}
        </>
    );
};

export default TaskCardGeneralTab;
